import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { BreedseekData } from "generated/graphql";
import { FC } from "react";

interface BreedseekDataTableProps {
  breedseekData: BreedseekData | null | undefined;
}

export const BreedseekDataTable: FC<BreedseekDataTableProps> = ({ breedseekData }) => {
  const {
    barcode = "",
    test,
    order,
    akaushi,
    angus,
    beefmasters,
    brangus,
    hereford,
    holstein,
    jersey,
    redAngus,
    santaGertrudis,
    wagyu,
  } = breedseekData || {};

  const tableData: BreedseekData[] = breedseekData
    ? [
        {
          barcode: barcode || "-",
          test: test || "-",
          order: order || "-",
          akaushi,
          angus,
          beefmasters,
          brangus,
          hereford,
          holstein,
          jersey,
          redAngus,
          santaGertrudis,
          wagyu,
        },
      ]
    : [];

  const columnHelper = createColumnHelper<BreedseekData>();
  const columns = [
    columnHelper.accessor("test", {
      id: "test",
      header: () => <span>Test</span>,
    }),
    columnHelper.accessor((row) => row.barcode, {
      id: "barcode",
      header: () => <span>Barcode</span>,
    }),
    columnHelper.accessor((row) => row.akaushi, {
      id: "akaushi",
      header: () => <span>Akaushi</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.angus, {
      id: "angus",
      header: () => <span>Angus</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.beefmasters, {
      id: "beefmasters",
      header: () => <span>Beefmasters</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.brangus, {
      id: "brangus",
      header: () => <span>Brangus</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.hereford, {
      id: "hereford",
      header: () => <span>Hereford</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.holstein, {
      id: "holstein",
      header: () => <span>Holstein</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.jersey, {
      id: "jersey",
      header: () => <span>Jersey</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.redAngus, {
      id: "redAngus",
      header: () => <span>Red Angus</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.santaGertrudis, {
      id: "santaGertrudis",
      header: () => <span>Santa Gertrudis</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.wagyu, {
      id: "wagyu",
      header: () => <span>Wagyu</span>,
      cell: (info) => `${info.getValue() * 100}%`,
    }),
    columnHelper.accessor((row) => row.order, {
      id: "order",
      header: () => <span>Order</span>,
    }),
  ];

  const isBreedVisible = (breed: string) => {
    const breedPercentage = (breedseekData || [])?.[breed];
    return breedPercentage * 100 !== 0;
  };
  const columnVisibility = {
    test: true,
    barcode: true,
    akaushi: isBreedVisible("akaushi"),
    angus: isBreedVisible("angus"),
    beefmasters: isBreedVisible("beefmasters"),
    brangus: isBreedVisible("brangus"),
    hereford: isBreedVisible("hereford"),
    holstein: isBreedVisible("holstein"),
    jersey: isBreedVisible("jersey"),
    redAngus: isBreedVisible("redAngus"),
    santaGertrudis: isBreedVisible("santaGertrudis"),
    wagyu: isBreedVisible("wagyu"),
    order: true,
  };

  const table = useReactTable({
    data: tableData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: columnVisibility,
    },
  });

  return (
    <>
      {breedseekData ? (
        <>
          <table className="w-full table-fixed shadow overflow-hidden rounded-lg">
            <thead className="bg-blue-700 text-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      style={{ borderRight: "1px solid #fff" }}
                      className="h-8 w-full px-4 bg-breedr-blue-500 text-left text-xs leading-8 uppercase"
                      key={header.id}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      style={{ border: "1px solid #f3f5f7" }}
                      className="px-4 bg-white text-xs leading-8 text-ellipsis overflow-hidden"
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : (
        <p>Upload Breedseek data for this animal to view it here.</p>
      )}
    </>
  );
};
